<template>
    <iframe
        v-if="iframeSource"
        :key="iframeSource"
        class="h-full w-full border-0"
        :src="iframeSource"
        title="PDF viewer"
    />
</template>

<script setup lang="ts">
import { notifyError } from '@/bugsnag';
import { annotatePDF } from '@/shared/utils/pdf/pdf-annotations-utils';
import type { PDFAnnotationInput } from '@/shared/utils/pdf/pdf-annotations.types';
import { asyncComputed } from '@vueuse/core';
import { computed, watch, onBeforeUnmount } from 'vue';

type PDFViewerProps = {
    source: Blob;
    annotations?: PDFAnnotationInput[];
};

type MaybeObjectUrl = ReturnType<typeof URL.createObjectURL> | undefined;

const props = withDefaults(defineProps<PDFViewerProps>(), { annotations: () => [] });

const focusPage = defineModel<number>('focusPage', { default: 1 });

const annotatedDocument = asyncComputed<MaybeObjectUrl>(async onCancel => {
    let cancelled = false;
    onCancel(() => {
        cancelled = true;
    });

    let document = props.source;
    if (props.annotations.length) {
        try {
            document = await annotatePDF(props.source, props.annotations);
        } catch (err) {
            notifyError(err, 'PDFViewer::annotate', {
                annotations: props.annotations,
            });
        }
    }

    return cancelled ? undefined : URL.createObjectURL(document);
});

const iframeSource = computed(() => (annotatedDocument.value ? `${annotatedDocument.value}#page=${focusPage.value}` : undefined));

// De-allocates Blob objects that point to a file
const revoke = (resource: MaybeObjectUrl) => {
    if (resource) {
        URL.revokeObjectURL(resource);
    }
};

// If the document changes we need to revoke the old object
watch(annotatedDocument, (_, oldObjectUrl) => revoke(oldObjectUrl));

// A new document opens on its first page
watch(
    () => props.source,
    () => {
        focusPage.value = 1;
    },
);

onBeforeUnmount(() => revoke(annotatedDocument.value));
</script>
